/**
 * Permission Flow View - Permission tracing and inheritance visualization
 *
 * FERP Principles:
 * - Data-driven: All data from backend via repository
 * - No hardcoded business logic
 * - Complete type safety
 * - Real-time permission tracing
 *
 * GitHub Issue: #340
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Stack,
  TextInput,
  ActionIcon,
  Alert,
  Table,
  Badge,
  Text,
  Group,
  ScrollArea,
  Box,
  Loader,
} from '@mantine/core';
import {
  IconShield,
  IconSearch,
  IconX,
  IconArrowRight,
  IconCheck,
} from '@tabler/icons-react';
import {
  createHierarchyRepository,
  type RoleHierarchy,
  type PermissionFlow,
  PermissionSource,
} from '../../data/repositories/HierarchyRepository';

export interface PermissionFlowViewProps {
  hierarchy: RoleHierarchy;
  tenantId: string;
}

export function PermissionFlowView({
  hierarchy,
  tenantId,
}: PermissionFlowViewProps) {
  const repository = useMemo(
    () => createHierarchyRepository(),
    []
  );

  const [permission, setPermission] = useState('');
  const [flow, setFlow] = useState<PermissionFlow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build role map for quick lookup
  const roleMap = useMemo(() => {
    return new Map(hierarchy.roles.map((role) => [role.roleId, role]));
  }, [hierarchy.roles]);

  // Get all unique permissions from hierarchy
  const allPermissions = useMemo(() => {
    const perms = new Set<string>();
    hierarchy.roles.forEach((role) => {
      role.effectivePermissions.forEach((p) => perms.add(p));
    });
    return Array.from(perms).sort();
  }, [hierarchy.roles]);

  // Load permission flow
  const loadFlow = useCallback(
    async (perm: string) => {
      if (!perm.trim()) {
        setFlow(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await repository.getPermissionFlow(tenantId, perm);
        setFlow(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load permission flow'
        );
      } finally {
        setLoading(false);
      }
    },
    [repository, tenantId]
  );

  // Get source color
  const getSourceColor = (source: PermissionSource): string => {
    switch (source) {
      case PermissionSource.Direct:
        return 'blue';
      case PermissionSource.Inherited:
        return 'green';
      case PermissionSource.Overridden:
        return 'orange';
      default:
        return 'gray';
    }
  };

  // Handle search
  const handleSearch = useCallback(() => {
    loadFlow(permission);
  }, [permission, loadFlow]);

  // Handle permission input change
  const handlePermissionChange = useCallback(
    (value: string) => {
      setPermission(value);
      // Auto-search after typing stops (debounced)
      const timer = setTimeout(() => {
        loadFlow(value);
      }, 500);
      return () => clearTimeout(timer);
    },
    [loadFlow]
  );

  return (
    <Stack gap="md">
      {/* Search Input */}
      <Stack gap="xs">
        <TextInput
          placeholder="Enter permission name (e.g., workflow:admin, user:read)"
          leftSection={<IconShield size={16} />}
          value={permission}
          onChange={(e) => handlePermissionChange(e.currentTarget.value)}
          rightSection={
            <Group gap={4}>
              {loading && <Loader size="xs" />}
              {permission && !loading && (
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    setPermission('');
                    setFlow(null);
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
              <ActionIcon variant="filled" onClick={handleSearch}>
                <IconSearch size={14} />
              </ActionIcon>
            </Group>
          }
        />

        {/* Available permissions hint */}
        {allPermissions.length > 0 && (
          <Text size="xs" c="dimmed">
            Available permissions: {allPermissions.slice(0, 5).join(', ')}
            {allPermissions.length > 5 && ` (+${allPermissions.length - 5} more)`}
          </Text>
        )}
      </Stack>

      {/* Error */}
      {error && (
        <Alert icon={<IconX size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* No permission entered */}
      {!permission && !flow && (
        <Alert icon={<IconSearch size={16} />} color="blue" variant="light">
          <Stack gap="xs">
            <Text fw={500}>Permission Flow Tracing</Text>
            <Text size="sm">
              Enter a permission name above to see which roles have it and how
              they inherited it through the hierarchy.
            </Text>
            <Text size="sm">
              <strong>Examples:</strong> workflow:admin, user:read, report:export
            </Text>
          </Stack>
        </Alert>
      )}

      {/* No roles with permission */}
      {flow && flow.rolesWithPermission.length === 0 && (
        <Alert icon={<IconX size={16} />} color="gray" variant="light">
          <Text>
            No roles have the permission <strong>&quot;{flow.permission}&quot;</strong>
          </Text>
        </Alert>
      )}

      {/* Permission flow results */}
      {flow && flow.rolesWithPermission.length > 0 && (
        <Stack gap="md">
          {/* Summary */}
          <Alert icon={<IconCheck size={16} />} color="green" variant="light">
            <Text size="sm">
              Found <strong>{flow.rolesWithPermission.length}</strong> roles
              with permission <strong>&quot;{flow.permission}&quot;</strong>
            </Text>
          </Alert>

          {/* Flow table */}
          <ScrollArea h={400}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Inheritance Path</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {flow.rolesWithPermission.map((node) => {
                  const role = roleMap.get(node.roleId);

                  return (
                    <Table.Tr key={node.roleId}>
                      {/* Role */}
                      <Table.Td>
                        <Text fw={500}>{node.roleName}</Text>
                        {role && (
                          <Text size="xs" c="dimmed">
                            {role.displayName}
                          </Text>
                        )}
                      </Table.Td>

                      {/* Category */}
                      <Table.Td>
                        {role && (
                          <Badge size="sm" color={role.category.toLowerCase()}>
                            {role.category}
                          </Badge>
                        )}
                      </Table.Td>

                      {/* Source */}
                      <Table.Td>
                        <Badge
                          size="sm"
                          color={getSourceColor(node.source)}
                          variant="light"
                        >
                          {node.source}
                        </Badge>
                      </Table.Td>

                      {/* Inheritance Path */}
                      <Table.Td>
                        {node.source === PermissionSource.Direct ? (
                          <Text size="sm" c="dimmed">
                            Directly assigned
                          </Text>
                        ) : (
                          <Group gap={4} wrap="nowrap">
                            {node.inheritancePath.map((roleId, index) => {
                              const pathRole = roleMap.get(roleId);
                              return (
                                <React.Fragment key={roleId}>
                                  {index > 0 && (
                                    <IconArrowRight size={12} color="gray" />
                                  )}
                                  <Badge
                                    size="xs"
                                    variant="outline"
                                    style={{
                                      fontWeight:
                                        roleId === node.roleId ? 700 : 400,
                                    }}
                                  >
                                    {pathRole?.roleName || roleId.slice(0, 8)}
                                  </Badge>
                                </React.Fragment>
                              );
                            })}
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          {/* Legend */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Source Types:
            </Text>
            <Group gap="md">
              <Group gap="xs">
                <Badge size="sm" color="blue" variant="light">
                  Direct
                </Badge>
                <Text size="xs" c="dimmed">
                  Permission directly assigned to role
                </Text>
              </Group>
              <Group gap="xs">
                <Badge size="sm" color="green" variant="light">
                  Inherited
                </Badge>
                <Text size="xs" c="dimmed">
                  Permission inherited from parent role
                </Text>
              </Group>
              <Group gap="xs">
                <Badge size="sm" color="orange" variant="light">
                  Overridden
                </Badge>
                <Text size="xs" c="dimmed">
                  Permission overridden from parent
                </Text>
              </Group>
            </Group>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
